import { useState } from "react";
import { Box, Button, Typography, IconButton, CircularProgress } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useForm } from "react-hook-form";
import { AsyncAutoCompleteNew } from "../../../components/async-auto-complete/AsyncAutoCompleteNew";
import { useUpdateRecord } from "../../../api/useUpdateRecord";
import { getStore } from "../../../store/store";
import { ORDERS_SINCE_DATE, ROLES } from "../../../utils/constants";
import { useUser } from "../../../api/useAuth";
import { useQuery } from "@tanstack/react-query";
import { getItems } from "../../../api/items";
import store2 from "store2";

type PaymentBankTransactionCellProps = {
    paymentId: number;
    connectedBankTransaction: any;
    onAssociationChange: () => void;
};

const ALLOWED_ROLES = [ROLES.admin, ROLES.shilan, ROLES.virtual_assistant];

const PAYMENT_DESCRIPTION_PATTERNS = ["POS VENDAS", "IFTHEN", "TRF", "TFI", "reembolso"];

const createDescriptionFilter = () => ({
    _or: PAYMENT_DESCRIPTION_PATTERNS.map((pattern) => ({
        description: { _icontains: pattern },
    })),
});

const formatAmount = (amount: string | number | undefined) => {
    if (amount === undefined || amount === null) return "0.00 €";
    const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(numericAmount)) return "0.00 €";
    return `${numericAmount.toFixed(2)} €`;
};

const formatDateToPortuguese = (dateString: string) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
};

const convertPortugueseDateToApi = (inputDate: string) => {
    const trimmedDate = inputDate.trim();
    const parts = trimmedDate.split("/");
    if (parts.length === 3) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    return trimmedDate;
};

const isTransactionObject = (value: any): value is { document_date: string; amount: string; description?: string } => {
    return value && typeof value === "object" && "document_date" in value;
};

const useBankTransaction = (transactionId: number | string | null) => {
    return useQuery({
        queryKey: ["bank_transaction", transactionId],
        queryFn: async () => {
            if (!transactionId) return null;
            const result = await getItems("bank_transactions", "id,document_date,amount,description", {
                filter: { id: { _eq: transactionId } },
                pageSize: 1,
            });
            return result.items[0] || null;
        },
        enabled: !!transactionId,
        staleTime: 5 * 60 * 1000,
    });
};

export const PaymentBankTransactionCell = ({
    paymentId,
    connectedBankTransaction,
    onAssociationChange,
}: PaymentBankTransactionCellProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const entityForm = useForm();
    const updateRecord = useUpdateRecord({ queryInvalidatePrefix: "orders_payments" });
    const { notifications } = getStore();
    const { data: user } = useUser();

    const hasTransactionId = !!connectedBankTransaction;
    const isFullObject = isTransactionObject(connectedBankTransaction);
    const transactionIdToFetch = hasTransactionId && !isFullObject ? connectedBankTransaction : null;
    
    const { data: fetchedTransaction, isLoading: isLoadingTransaction } = useBankTransaction(transactionIdToFetch);
    
    const transactionData = isFullObject ? connectedBankTransaction : fetchedTransaction;

    const hasRolePermission = user?.role?.id && ALLOWED_ROLES.includes(user.role.id);
    const hasFinanceAuth = !!store2.get("finance_auth");

    if (!hasRolePermission || !hasFinanceAuth) return null;

    const handleAssociate = async () => {
        const selectedTransaction = entityForm.getValues("bank_transaction");
        if (!selectedTransaction) return;

        try {
            await updateRecord.mutateAsync({
                id: paymentId,
                entity: "orders_payments",
                data: {
                    connected_bank_transaction_id: selectedTransaction.id,
                },
            });

            notifications.setNotification({
                isOpen: true,
                message: "Transação bancária associada com sucesso",
                severity: "success",
            });

            setIsEditing(false);
            onAssociationChange();
        } catch {
            notifications.setNotification({
                isOpen: true,
                message: "Não foi possível associar a transação bancária",
                severity: "error",
            });
        }
    };

    const createCustomFilter = (value: string) => {
        const apiDate = convertPortugueseDateToApi(value);
        return {
            _and: [
                { document_date: { _gte: ORDERS_SINCE_DATE } },
                { document_date: { _eq: apiDate } },
                createDescriptionFilter(),
            ],
        };
    };

    const renderTransactionLabel = (option: any) => {
        if (!option) return "";
        const dateFormatted = formatDateToPortuguese(option.document_date);
        const description = option.description ? ` - ${option.description}` : "";
        return `${dateFormatted} - ${formatAmount(option.amount)}${description}`;
    };

    if (hasTransactionId && !isEditing) {
        if (isLoadingTransaction) {
            return <CircularProgress size={20} />;
        }

        if (transactionData) {
            const dateFormatted = formatDateToPortuguese(transactionData.document_date);
            const description = transactionData.description ? ` - ${transactionData.description}` : "";
            
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">
                        {dateFormatted} - {formatAmount(transactionData.amount)}{description}
                    </Typography>
                    <IconButton size="small" onClick={() => setIsEditing(true)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Box>
            );
        }

        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="error">
                    Transação #{connectedBankTransaction} (não encontrada)
                </Typography>
                <IconButton size="small" onClick={() => setIsEditing(true)}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 280 }}>
            <AsyncAutoCompleteNew
                entityForm={entityForm}
                fieldName="bank_transaction"
                placeholder="Pesquise por data (DD/MM/AAAA)"
                recordsOptions={{
                    entity: "bank_transactions",
                    fieldsToQuery: "id,document_date,amount,description",
                    customFilter: createCustomFilter,
                    labelKey: renderTransactionLabel,
                }}
            />
            <Button size="small" variant="contained" onClick={handleAssociate}>
                Associar
            </Button>
            {isEditing && (
                <Button size="small" variant="outlined" onClick={() => setIsEditing(false)}>
                    Cancelar
                </Button>
            )}
        </Box>
    );
};
